/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICommerceApiSettings,
    IDictionary
} from '@msdyn365-commerce/core';
import { getByIdsAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { SimpleProduct } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';

import { getProductImageUrls } from '../utils/product-list-utils';

const buildCacheKey = (base: string, apiSettings: ICommerceApiSettings): string => {
    return `${base}-chanId:${apiSettings.channelId}-catId:${apiSettings.catalogId}`;
};

/**
 * Product Input
 */
export class ProductInput implements IActionInput {
    public productId: number;
    public channelId: number;
    private apiSettings: ICommerceApiSettings;

    constructor(productId: number | string, apiSettings: ICommerceApiSettings) {
        this.apiSettings = apiSettings;
        this.productId = +productId;
        this.channelId = apiSettings.channelId;
    }

    public getCacheKey = () => buildCacheKey(`RecordId-${this.productId.toString()}`, this.apiSettings);
    public getCacheObjectType = () => 'SimpleProduct';
    public dataCacheType = (): CacheType => 'application';
}

/**
 * Calls the Retail API and returns the product based on the passed ProductInput
 */
export async function getSimpleProductsAction(inputs: ProductInput[], ctx: IActionContext): Promise<SimpleProduct[]> {
    if (!inputs || !Array.isArray(inputs) || inputs.length === 0) {
        ctx.trace('[getSimpleProductsAction] Invalid or empty inputs passed.');
        return [];
    }

    const productIdMapping: IDictionary<number> = {};
    const productIds = inputs.map((input, idx) => {
        productIdMapping[input.productId] = idx;
        return input.productId;
    });

    return getByIdsAsync({ callerContext: ctx }, inputs[0].channelId, productIds).then(products => {
        if (!products) {
            return [];
        }
        const mappedProducts = (<SimpleProduct[]>getProductImageUrls(products, ctx.requestContext.apiSettings)).reduce(
            (memo: SimpleProduct[], product: SimpleProduct | undefined) => {
                if (!product) {
                    return memo;
                }
                const idx = productIdMapping[product.RecordId];
                memo[idx] = product;
                return memo;
            },
            []
        );

        // NOTE: <ZAFINE> This is not an ideal fix, but will resolve all current issues
        return inputs.map(input => {
            const foundProduct = mappedProducts.find(product => product && product.RecordId === input.productId);
            return foundProduct || <SimpleProduct>{};
        });
    });
}

export default createObservableDataAction({
    action: <IAction<SimpleProduct[]>>getSimpleProductsAction,
    id: '@msdyn365-commerce/get-simple-products',
    isBatched: true
});
